import { useState } from 'react';
import LessonCard from './components/LessonCard';
import { grade7Lessons } from './data/lessonsData';
import SEOHead from '../../../components/SEOHead';
export default function Grade7Page() {
  const [activeTab, setActiveTab] = useState<'lessons' | 'worksheets'>('lessons');
  const siteUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';
  return (
    <div className="min-h-screen bg-gradient-to-br from-lime-50 via-green-50 to-emerald-50">
      <SEOHead title="Grade 7 Math New York | Proportional Relationships Integers Probability | Augustine Anyimadu-Ahenkae" description="Grade 7 math lessons on proportional relationships, integers, probability, and geometric constructions. Common Core Grade 7 aligned interactive lessons and worksheets." canonical="/grades/grade-7" keywords="Grade 7 math, proportional relationships, integers, probability, Common Core grade 7, 7th grade math" jsonLd={{'@context':'https://schema.org','@type':'Course',name:'Grade 7 Math',url:`${siteUrl}/grades/grade-7`,provider:{'@type':'Person',name:'Augustine Anyimadu-Ahenkae'},educationalLevel:'Grade 7'}} />
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-lime-500 to-green-600 rounded-2xl flex items-center justify-center">
              <i className="ri-number-7 text-3xl text-white"></i>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Grade 7 Math</h1>
              <p className="text-gray-600 mt-1">Developing proportional reasoning and algebraic skills</p>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 mt-6">
            <button
              onClick={() => setActiveTab('lessons')}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === 'lessons'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Lessons
            </button>
            <button
              onClick={() => setActiveTab('worksheets')}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === 'worksheets'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Worksheets
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {activeTab === 'lessons' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {grade7Lessons.map((lesson) => (
              <LessonCard key={lesson.lessonNumber} lesson={lesson} />
            ))}
          </div>
        )}

        {activeTab === 'worksheets' && (
          <div className="space-y-8">
            {/* Worksheet Preview Banner */}
            <div className="bg-gradient-to-r from-lime-500 to-green-600 rounded-2xl p-8 text-white">
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <i className="ri-file-text-line text-2xl"></i>
                    <h2 className="text-2xl font-bold">Practice Worksheets Available</h2>
                  </div>
                  <p className="text-lime-100 mb-6">
                    Download our free sample worksheet and explore more practice materials for Grade 7
                  </p>
                  
                  {/* Free Sample Card */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 mb-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-1 bg-green-400 text-green-900 rounded-md text-xs font-bold">
                            FREE SAMPLE
                          </span>
                        </div>
                        <h3 className="text-lg font-bold mb-2">Solving Multi-Step Equations</h3>
                        <p className="text-lime-100 text-sm mb-3">
                          Master techniques for solving equations with variables on both sides and distributive property
                        </p>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="flex items-center gap-1">
                            <i className="ri-file-line"></i>
                            11 pages
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-checkbox-circle-line"></i>
                            42 problems
                          </span>
                        </div>
                      </div>
                      <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center">
                        <i className="ri-download-cloud-line text-3xl"></i>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => alert('Download will start shortly!')}
                      className="px-6 py-3 bg-white text-green-600 rounded-lg font-semibold hover:bg-lime-50 transition-all flex items-center gap-2 whitespace-nowrap"
                    >
                      <i className="ri-download-line"></i>
                      Download Free Sample
                    </button>
                    <a
                      href="/worksheets?category=grade-7"
                      className="px-6 py-3 bg-green-700 text-white rounded-lg font-semibold hover:bg-green-800 transition-all flex items-center gap-2 whitespace-nowrap"
                    >
                      View All Worksheets
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Membership Upsell */}
            <div className="bg-white rounded-xl p-8 border-2 border-green-200">
              <div className="text-center max-w-2xl mx-auto">
                <div className="w-16 h-16 bg-gradient-to-br from-lime-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-vip-crown-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Unlock 90+ Grade 7 Worksheets
                </h3>
                <p className="text-gray-600 mb-6">
                  Get access to our complete collection covering proportions, equations, inequalities, geometry, and probability with comprehensive solutions
                </p>
                <a
                  href="/#membership"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-lime-500 to-green-600 text-white rounded-lg font-semibold hover:from-lime-600 hover:to-green-700 transition-all whitespace-nowrap"
                >
                  Choose Your Plan
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}